if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.warmUpMedia();
        if (!this.elements.thumbnails) return;

        this.syncThumbnailRailHeight = this.syncThumbnailRailHeight.bind(this);
        if ('ResizeObserver' in window) {
          this.thumbnailHeightObserver = new ResizeObserver(this.syncThumbnailRailHeight);
          this.thumbnailHeightObserver.observe(this.elements.viewer);
        } else {
          window.addEventListener('resize', this.syncThumbnailRailHeight);
        }
        this.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', this.syncThumbnailRailHeight, { once: true });
        });
        requestAnimationFrame(this.syncThumbnailRailHeight);

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Fotos fora do 1º slide são lazy e, no layout com miniaturas, ficam escondidas
      // até o clique: só começavam a baixar na troca (slide em branco). Com a página
      // ociosa, libera o download para a troca ser imediata.
      warmUpMedia() {
        const run = () => {
          this.elements.viewer
            ?.querySelectorAll('.product__media img[loading="lazy"]')
            .forEach((img) => (img.loading = 'eager'));
        };
        const schedule = () => {
          if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
          else window.setTimeout(run, 1500);
        };
        if (document.readyState === 'complete') schedule();
        else window.addEventListener('load', schedule, { once: true });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        requestAnimationFrame(this.syncThumbnailRailHeight);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const thumbnailSlider = this.elements.thumbnails.slider;
        const sliderStyle = thumbnailSlider ? window.getComputedStyle(thumbnailSlider) : null;
        const isVerticalSlider =
          thumbnailSlider &&
          sliderStyle?.flexDirection === 'column' &&
          thumbnailSlider.scrollHeight > thumbnailSlider.clientHeight;

        if (isVerticalSlider) {
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = thumbnailSlider.scrollTop;
          const visibleBottom = visibleTop + thumbnailSlider.clientHeight;

          if (thumbnailTop < visibleTop || thumbnailBottom > visibleBottom) {
            thumbnailSlider.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          }

          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        thumbnailSlider.scrollTo({ left: thumbnail.offsetLeft });
      }

      syncThumbnailRailHeight() {
        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        const mediaContainer =
          activeMedia.querySelector('.product-media-container') ||
          activeMedia.querySelector('.product__modal-opener') ||
          activeMedia;
        const mediaHeight = Math.round(mediaContainer.getBoundingClientRect().height);
        if (mediaHeight > 0) {
          this.style.setProperty('--vh-gallery-media-height', `${mediaHeight}px`);
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
